import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Plus, Search, AlertTriangle, CheckCircle2, Clock, Wrench, Package, Store as StoreIcon, MessageSquare } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { Can } from '../lib/usePermissions';
import { ticketsApi, storesApi, usersApi, inventoryApi } from '../lib/api';
import PublicHeader from '../components/PublicHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';

const STATUS_LABELS = {
  open: 'Aperto',
  in_progress: 'In lavorazione',
  waiting_parts: 'In attesa ricambi',
  resolved: 'Risolto',
  closed: 'Chiuso',
};
const STATUS_TONE = {
  open: 'bg-red-50 text-red-700 border-red-200',
  in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
  waiting_parts: 'bg-amber-50 text-amber-700 border-amber-200',
  resolved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  closed: 'bg-slate-50 text-slate-600 border-slate-200',
};
const PRIORITY_LABELS = { low: 'Bassa', normal: 'Normale', high: 'Alta', urgent: 'Urgente' };
const PRIORITY_TONE = {
  low: 'bg-slate-100 text-slate-700',
  normal: 'bg-slate-100 text-slate-700',
  high: 'bg-orange-100 text-orange-800',
  urgent: 'bg-red-100 text-red-800 font-semibold',
};

export default function AssistancePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [stores, setStores] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || '');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterStore, setFilterStore] = useState(searchParams.get('store_id') || '');
  const [includeClosed, setIncludeClosed] = useState(false);
  const [showCreate, setShowCreate] = useState(
    !!location.state?.openCreate || searchParams.get('new') === '1'
  );
  const [prefill, setPrefill] = useState(location.state?.prefill || null);

  const isOps = user?.role === 'operations_manager';
  const isAM = user?.role === 'area_manager';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterPriority) params.priority = filterPriority;
      if (filterStore) params.store_id = filterStore;
      if (includeClosed) params.include_closed = true;

      const [t, st, s, u, c] = await Promise.all([
        ticketsApi.list(params).then(r => r.data),
        ticketsApi.stats().then(r => r.data),
        storesApi.list().then(r => r.data),
        (isOps || isAM) ? usersApi.list().then(r => r.data).catch(() => []) : Promise.resolve([]),
        inventoryApi.categories().then(r => r.data).catch(() => []),
      ]);
      setTickets(t || []);
      setStats(st);
      setStores(Array.isArray(s) ? s : (s?.stores || []));
      setUsers(u || []);
      setCategories(c || []);
    } catch (e) {
      toast.error('Errore caricamento ticket');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterPriority, filterStore, includeClosed, isOps, isAM]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!search.trim()) return tickets;
    const q = search.toLowerCase();
    return tickets.filter(t => (
      `${t.title} ${t.description} ${t.store_name || ''} ${t.item_code || ''}`.toLowerCase().includes(q)
    ));
  }, [tickets, search]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="assistance-page">
      <PublicHeader
        copyEnabled={false}
        actions={
          <Can permission="tickets.create">
            <Button onClick={() => setShowCreate(true)} data-testid="new-ticket-btn" size="sm" className="h-8 rounded-md whitespace-nowrap" aria-label="Nuovo ticket">
              <Plus className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Nuovo ticket</span>
            </Button>
          </Can>
        }
      />

      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 md:py-10 space-y-8">
        {/* TITOLO PAGINA */}
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Ticket attivi</p>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mt-1">
            Gestione assistenza tecnica
          </h1>
          <p className="text-sm text-slate-500 mt-2 max-w-2xl">
            Apri ticket per guasti, riparazioni e sostituzioni. Lo stato di ogni intervento è sempre visibile a Operations Manager, Area Manager e collaboratori del negozio coinvolto.
          </p>
        </div>

      {/* KPI */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Aperti" value={stats.open} tone="red" testId="stat-open"
                    onClick={() => { setFilterStatus('open'); setSearchParams({ status: 'open' }); }} />
          <StatCard label="In lavorazione" value={stats.in_progress} tone="blue" testId="stat-in-progress"
                    onClick={() => { setFilterStatus('in_progress'); setSearchParams({ status: 'in_progress' }); }} />
          <StatCard label="In attesa ricambi" value={stats.waiting_parts} tone="amber" testId="stat-waiting"
                    onClick={() => { setFilterStatus('waiting_parts'); setSearchParams({ status: 'waiting_parts' }); }} />
          <StatCard label="Risolti questo mese" value={stats.resolved_this_month} tone="green" testId="stat-resolved-month" />
        </div>
      )}

      {/* SEARCH + FILTRI */}
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="ticket-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per titolo, descrizione, negozio, codice oggetto..."
            className="pl-10 h-11 rounded-md"
          />
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <CompactFilter label="Stato" value={filterStatus} onChange={(v) => { setFilterStatus(v); v ? setSearchParams({ status: v }) : setSearchParams({}); }}
                         options={Object.entries(STATUS_LABELS).map(([k, v]) => ({ id: k, name: v }))} />
          <CompactFilter label="Priorità" value={filterPriority} onChange={setFilterPriority}
                         options={Object.entries(PRIORITY_LABELS).map(([k, v]) => ({ id: k, name: v }))} />
          <CompactFilter label="Negozio" value={filterStore} onChange={setFilterStore}
                         options={stores.map(s => ({ id: s.store_id, name: s.display_name || s.name }))} />
          <label className="text-xs text-slate-600 flex items-center gap-1.5 ml-2" data-testid="include-closed-toggle">
            <input type="checkbox" checked={includeClosed} onChange={e => setIncludeClosed(e.target.checked)}
                   className="w-3.5 h-3.5 accent-slate-900" />
            Mostra chiusi
          </label>
          {(filterStatus || filterPriority || filterStore) && (
            <button onClick={() => { setFilterStatus(''); setFilterPriority(''); setFilterStore(''); setSearchParams({}); }}
                    className="text-xs text-slate-500 hover:text-slate-800 underline px-2" data-testid="clear-ticket-filters">
              Reset
            </button>
          )}
        </div>
      </div>

      {/* LISTA */}
      <div className="space-y-3">
        {loading ? (
          <div className="p-8 text-center text-slate-500 border rounded-md bg-white">Caricamento…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center border rounded-md bg-white">
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-300" />
            <p className="mt-3 text-slate-600">Nessun ticket trovato.</p>
            <Button onClick={() => setShowCreate(true)} variant="link" className="mt-2">
              Apri il primo ticket
            </Button>
          </div>
        ) : (
          filtered.map(t => <TicketRow key={t.ticket_id} ticket={t} onOpen={() => navigate(`/assistance/${t.ticket_id}`)} />)
        )}
      </div>

      {showCreate && (
        <CreateTicketDialog
          stores={stores}
          users={users}
          categories={categories}
          prefilledStoreId={prefill?.store_id}
          prefilledItemId={prefill?.item_id}
          onClose={() => { setShowCreate(false); setPrefill(null); }}
          onCreated={(tk) => { setShowCreate(false); setPrefill(null); load(); navigate(`/assistance/${tk.ticket_id}`); }}
        />
      )}
      </div>
    </div>
  );
}

function StatCard({ label, value, tone, testId, onClick }) {
  const palette = tone === 'red' ? 'border-red-200 hover:border-red-400'
                : tone === 'blue' ? 'border-blue-200 hover:border-blue-400'
                : tone === 'amber' ? 'border-amber-200 hover:border-amber-400'
                : tone === 'green' ? 'border-emerald-200 hover:border-emerald-400'
                : 'border-slate-200';
  const Comp = onClick ? 'button' : 'div';
  return (
    <Comp
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={`bg-white border rounded-lg p-5 text-left transition ${palette} ${onClick ? 'cursor-pointer hover:shadow-sm' : ''}`}
      data-testid={testId}
    >
      <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</p>
      <p className="mt-3 text-3xl font-bold tracking-tight text-slate-900 font-mono">{value}</p>
    </Comp>
  );
}

function CompactFilter({ label, value, onChange, options }) {
  return (
    <Select value={value || 'all'} onValueChange={(v) => onChange(v === 'all' ? '' : v)}>
      <SelectTrigger className="h-9 w-auto min-w-[140px] rounded-full bg-white text-xs px-3" data-testid={`ticket-filter-${label.toLowerCase()}`}>
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">Tutti — {label}</SelectItem>
        {options.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function TicketRow({ ticket, onOpen }) {
  const created = new Date(ticket.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
  const isUrgent = ticket.priority === 'urgent' || ticket.priority === 'high';
  return (
    <button
      onClick={onOpen}
      className="w-full text-left bg-white border border-slate-200 rounded-lg p-5 hover:border-slate-400 hover:shadow-sm transition"
      data-testid={`ticket-row-${ticket.ticket_id}`}
    >
      <div className="flex items-start gap-4">
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            {isUrgent && <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />}
            <h3 className="font-heading text-base font-bold text-slate-900 leading-tight">{ticket.title}</h3>
            <span className={`text-[10px] px-2 py-0.5 rounded ${PRIORITY_TONE[ticket.priority]}`}>
              {PRIORITY_LABELS[ticket.priority]}
            </span>
          </div>
          <p className="text-sm text-slate-600 line-clamp-2">{ticket.description}</p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600">
            <span className="inline-flex items-center gap-1.5">
              <StoreIcon className="w-3.5 h-3.5 text-slate-400" />{ticket.store_name}
            </span>
            {(ticket.item_codes?.length > 0 || ticket.item_code) && (
              <span className="inline-flex items-center gap-1.5">
                <Package className="w-3.5 h-3.5 text-slate-400" />
                <code className="font-mono">{(ticket.item_codes && ticket.item_codes.length ? ticket.item_codes : [ticket.item_code]).join(', ')}</code>
              </span>
            )}
            <span className="inline-flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-slate-400" />{created}
            </span>
            {ticket.assignee_email && (
              <span className="text-slate-500">→ {ticket.assignee_email}</span>
            )}
            {ticket.comment_count > 0 && (
              <span className="inline-flex items-center gap-1.5 text-slate-500">
                <MessageSquare className="w-3.5 h-3.5" />{ticket.comment_count}
              </span>
            )}
          </div>
        </div>
        <span className={`inline-block px-3 py-1 rounded-full border text-xs font-medium ${STATUS_TONE[ticket.status]} shrink-0`}
              data-testid={`ticket-status-${ticket.ticket_id}`}>
          {STATUS_LABELS[ticket.status]}
        </span>
      </div>
    </button>
  );
}

function CreateTicketDialog({ stores, users, categories, onClose, onCreated, prefilledStoreId, prefilledItemId }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    store_id: prefilledStoreId || '',
    item_ids: prefilledItemId ? [prefilledItemId] : [],
    title: '',
    description: '',
    category: '',
    priority: 'normal',
    due_date: '',
    assignee_id: '',
  });
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const assignableUsers = users.filter(u => u.role === 'operations_manager' || u.role === 'area_manager');

  useEffect(() => {
    if (form.store_id) {
      inventoryApi.listItems({ store_id: form.store_id }).then(r => setItems(r.data || [])).catch(() => setItems([]));
    } else {
      setItems([]);
    }
  }, [form.store_id]);

  const toggleItem = (id) => setForm(f => ({
    ...f,
    item_ids: f.item_ids.includes(id) ? f.item_ids.filter(x => x !== id) : [...f.item_ids, id],
  }));

  async function submit() {
    if (!form.store_id) { toast.error('Seleziona un negozio'); return; }
    if (!form.title.trim() || form.title.trim().length < 2) { toast.error('Titolo troppo corto'); return; }
    if (!form.description.trim()) { toast.error('Descrizione obbligatoria'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.item_ids.length) delete payload.item_ids;
      Object.keys(payload).forEach(k => { if (payload[k] === '' || payload[k] == null) delete payload[k]; });
      const res = await ticketsApi.create(payload);
      toast.success('Ticket aperto');
      onCreated(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore creazione ticket');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Nuovo ticket di assistenza</DialogTitle>
          <DialogDescription>
            Compila Negozio e descrivi il guasto. Se serve, collega uno o più oggetti del Magazzino per tenere allineato lo storico.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          <section>
            <h4 className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold mb-3 pb-2 border-b border-slate-200">Dove?</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Negozio *</Label>
                <Select value={form.store_id} onValueChange={(v) => setForm(f => ({ ...f, store_id: v, item_ids: [] }))}>
                  <SelectTrigger data-testid="ticket-store"><SelectValue placeholder="— seleziona —" /></SelectTrigger>
                  <SelectContent>
                    {stores.map(s => <SelectItem key={s.store_id} value={s.store_id}>{s.display_name || s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Oggetti Magazzino (opzionale)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-between font-normal h-10" data-testid="ticket-items-trigger" disabled={!form.store_id}>
                      <span className="truncate text-sm">
                        {form.item_ids.length === 0
                          ? '— nessun oggetto specifico —'
                          : form.item_ids.length === 1
                            ? (items.find(i => i.item_id === form.item_ids[0])?.code || '1 oggetto')
                            : `${form.item_ids.length} oggetti selezionati`}
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-0" align="start">
                    <div className="max-h-64 overflow-y-auto py-1">
                      {items.length === 0 && (
                        <div className="px-3 py-4 text-xs text-slate-500">Nessun oggetto in questo negozio.</div>
                      )}
                      {items.map(i => (
                        <label key={i.item_id} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50 cursor-pointer"
                               data-testid={`ticket-item-option-${i.item_id}`}>
                          <input type="checkbox" checked={form.item_ids.includes(i.item_id)}
                                 onChange={() => toggleItem(i.item_id)} className="w-3.5 h-3.5 accent-slate-900" />
                          <span className="truncate"><code className="font-mono text-xs">{i.code}</code> — {i.model || i.category_name}</span>
                        </label>
                      ))}
                    </div>
                    {form.item_ids.length > 0 && (
                      <div className="border-t border-slate-100 px-3 py-1.5 text-right">
                        <button type="button" className="text-xs text-slate-600 hover:text-slate-900 underline"
                                onClick={() => setForm(f => ({ ...f, item_ids: [] }))} data-testid="ticket-items-clear">
                          Deseleziona tutti
                        </button>
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </section>

          <section>
            <h4 className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold mb-3 pb-2 border-b border-slate-200">Cosa serve?</h4>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Titolo *</Label>
                <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                       placeholder="Es. Lavatrice perde acqua, asciugacapelli non si accende..."
                       data-testid="ticket-title" />
              </div>
              <div>
                <Label className="text-xs">Descrizione *</Label>
                <Textarea rows={4} value={form.description}
                          onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                          placeholder="Descrivi il guasto, quando si manifesta, cosa hai già provato..."
                          data-testid="ticket-description" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs">Categoria</Label>
                  <Select value={form.category || 'none'} onValueChange={(v) => setForm(f => ({ ...f, category: v === 'none' ? '' : v }))}>
                    <SelectTrigger data-testid="ticket-category"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— nessuna —</SelectItem>
                      {['elettrodomestico', 'arredo', 'impianto', 'attrezzatura', 'altro'].map(c => (
                        <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Priorità</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm(f => ({ ...f, priority: v }))}>
                    <SelectTrigger data-testid="ticket-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Bassa</SelectItem>
                      <SelectItem value="normal">Normale</SelectItem>
                      <SelectItem value="high">Alta</SelectItem>
                      <SelectItem value="urgent">Urgente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Scadenza</Label>
                  <Input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
                         data-testid="ticket-due" />
                </div>
              </div>
              {assignableUsers.length > 0 && (
                <div>
                  <Label className="text-xs">Assegna a</Label>
                  <Select value={form.assignee_id || 'none'} onValueChange={(v) => setForm(f => ({ ...f, assignee_id: v === 'none' ? '' : v }))}>
                    <SelectTrigger data-testid="ticket-assignee"><SelectValue placeholder="— nessuno —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— nessuno per ora —</SelectItem>
                      {assignableUsers.map(u => (
                        <SelectItem key={u.user_id} value={u.user_id}>
                          {u.email} ({u.role === 'operations_manager' ? 'OPS' : 'AM'})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={submit} disabled={saving} data-testid="ticket-submit">
            {saving ? 'Salvataggio…' : 'Apri ticket'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Export del CreateTicketDialog per riusarlo dalla scheda oggetto Magazzino
export { CreateTicketDialog };
